import { useContractorAuthStore } from './contractorAuth';
import type { ContractorAuthResponse } from './contractorAuth';

import { API_BASE } from './apiBase';

interface ApiError {
  error: { code: string; message: string };
}

class ContractorApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const store = useContractorAuthStore.getState();
  const token = store.accessToken;

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> ?? {}),
    },
  });

  if (res.status === 401) {
    // Try refresh once
    const refresh = await fetch(`${API_BASE}/contractor/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!refresh.ok) {
      store.logout();
      throw new ContractorApiError('Session expired', 'session_expired', 401);
    }
    const refreshData = await refresh.json() as ContractorAuthResponse;
    store.setAuth(refreshData.contractor, refreshData.access_token, refreshData.expires_in);

    // Retry with new token
    const retry = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshData.access_token}`,
        ...(init.headers as Record<string, string> ?? {}),
      },
    });
    if (!retry.ok) {
      const err = await retry.json() as ApiError;
      throw new ContractorApiError(err.error?.message ?? 'Request failed', err.error?.code ?? 'unknown', retry.status);
    }
    return retry.json() as Promise<T>;
  }

  if (!res.ok) {
    const err = await res.json() as ApiError;
    throw new ContractorApiError(err.error?.message ?? 'Request failed', err.error?.code ?? 'unknown', res.status);
  }

  return res.json() as Promise<T>;
}

export const contractorApi = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : null }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : null }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body !== undefined ? JSON.stringify(body) : null }),
};
